import { useState, useCallback, useEffect } from 'react';
import { ViewsAPI } from '../../../utils/api';

/**
 * useSavedViews Hook
 * 
 * Manages saved view state and CRUD operations.
 * Persists views to backend via ViewsAPI.
 */
export function useSavedViews({ activeView, onSelectView: onSelectViewProp, selectedSprintId }) {
  // Saved Views state - start empty, API will provide views
  const [savedViews, setSavedViews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [captureState, setCaptureState] = useState(null);

  // Derive selectedViewId from activeView prop
  const selectedViewId = activeView?.id || null;

  // Sync savedViews when activeView changes (e.g., renamed from main view)
  // This ensures the sidebar shows the updated name/icon immediately
  useEffect(() => {
    if (activeView?.id) {
      setSavedViews(prev => prev.map(v => 
        v.id === activeView.id ? { ...v, name: activeView.name, icon: activeView.icon } : v
      ));
    }
  }, [activeView?.id, activeView?.name, activeView?.icon]);

  // Load views from API on mount
  useEffect(() => {
    let cancelled = false;

    const loadViews = async () => {
      try {
        setIsLoading(true);
        const views = await ViewsAPI.list();
        if (!cancelled) {
          setSavedViews(views || []);
          setError(null);
        }
      } catch (err) {
        console.error('[useSavedViews] Failed to load views:', err);
        if (!cancelled) {
          // Keep empty on error - no fallback views
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadViews();

    return () => {
      cancelled = true;
    };
  }, []);

  // View handlers - call App-level handler if provided
  const handleSelectView = useCallback((view) => {
    if (onSelectViewProp) {
      onSelectViewProp(view);
    }
  }, [onSelectViewProp]);

  const handleRenameView = useCallback(async (viewId, newName) => {
    // Optimistic update - also update activeView if it's the one being renamed
    setSavedViews(prev => prev.map(v => 
      v.id === viewId ? { ...v, name: newName, updatedAt: new Date().toISOString() } : v
    ));
    
    // If the active view is being renamed, update it in App state too
    if (activeView?.id === viewId && onSelectViewProp) {
      onSelectViewProp({ ...activeView, name: newName });
    }

    try {
      const view = savedViews.find(v => v.id === viewId);
      if (view) {
        await ViewsAPI.update(viewId, { ...view, name: newName });
      }
    } catch (err) {
      console.error('[useSavedViews] Failed to rename view:', err);
      // Reload views to get correct state
      const views = await ViewsAPI.list();
      setSavedViews(views || []);
    }
  }, [savedViews, activeView, onSelectViewProp]);

  const handleViewIconChange = useCallback(async (viewId, newIcon) => {
    // Optimistic update
    setSavedViews(prev => prev.map(v => 
      v.id === viewId ? { ...v, icon: newIcon, updatedAt: new Date().toISOString() } : v
    ));
    
    // If the active view is being updated, update it in App state too
    if (activeView?.id === viewId && onSelectViewProp) {
      onSelectViewProp({ ...activeView, icon: newIcon });
    }

    try {
      const view = savedViews.find(v => v.id === viewId);
      if (view) {
        await ViewsAPI.update(viewId, { ...view, icon: newIcon });
      }
    } catch (err) {
      console.error('[useSavedViews] Failed to update view icon:', err);
      // Reload views to get correct state
      const views = await ViewsAPI.list();
      setSavedViews(views || []);
    }
  }, [savedViews, activeView, onSelectViewProp]);

  const handleDeleteView = useCallback(async (viewId) => {
    setSavedViews(prev => prev.filter(v => v.id !== viewId));
    
    // If deleting the active view, clear it
    if (selectedViewId === viewId && onSelectViewProp) {
      onSelectViewProp(null);
    }

    try {
      await ViewsAPI.delete(viewId);
    } catch (err) {
      console.error('[useSavedViews] Failed to delete view:', err);
      // On error, reload views to get correct state
      const views = await ViewsAPI.list();
      setSavedViews(views || []);
    }
  }, [selectedViewId, onSelectViewProp]);

  const handleDuplicateView = useCallback(async (view) => {
    const newView = {
      ...view,
      id: undefined, // Let backend generate ID
      name: `${view.name} (Copy)`,
      isBuiltIn: false,
    };

    try {
      const created = await ViewsAPI.create(newView);
      setSavedViews(prev => [...prev, created]);
    } catch (err) {
      console.error('[useSavedViews] Failed to duplicate view:', err);
    }
  }, []);

  // Open modal for creating a new view
  const handleCreateView = useCallback(() => {
    setEditingView(null);
    setCaptureState(null);
    setViewModalOpen(true);
  }, []);

  // Open modal to capture current state as a view
  const handleCaptureView = useCallback(() => {
    setEditingView(null);
    // Capture current app state - for now use selected sprint
    setCaptureState({
      campaignId: selectedSprintId,
      filters: {},
    });
    setViewModalOpen(true);
  }, [selectedSprintId]);

  // Open modal to edit an existing view
  const handleEditView = useCallback((view) => {
    setEditingView(view);
    setCaptureState(null);
    setViewModalOpen(true);
  }, []);

  // Save view from modal (create or update)
  const handleSaveView = useCallback(async (view) => {
    try {
      const existingIndex = savedViews.findIndex(v => v.id === view.id);
      
      if (existingIndex >= 0) {
        // Update existing
        const updated = await ViewsAPI.update(view.id, view);
        setSavedViews(prev => {
          const newViews = [...prev];
          newViews[existingIndex] = updated;
          return newViews;
        });
      } else {
        // Create new
        const created = await ViewsAPI.create(view);
        setSavedViews(prev => [...prev, created]);
        
        // Automatically navigate to the newly created view
        if (onSelectViewProp) {
          onSelectViewProp(created);
        }
      }
    } catch (err) {
      console.error('[useSavedViews] Failed to save view:', err);
      // Still close modal, but show error somehow
    }

    setViewModalOpen(false);
    setEditingView(null);
    setCaptureState(null);
  }, [savedViews, onSelectViewProp]);

  // Close modal
  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setEditingView(null);
    setCaptureState(null);
  }, []);

  return {
    // State
    savedViews,
    selectedViewId,
    viewModalOpen,
    editingView,
    captureState,
    isLoading,
    error,
    
    // Handlers
    handleSelectView,
    handleRenameView,
    handleViewIconChange,
    handleDeleteView,
    handleDuplicateView,
    handleCreateView,
    handleCaptureView,
    handleEditView,
    handleSaveView,
    handleCloseViewModal,
  };
}

export default useSavedViews;
